import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar'; 
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import {
  addCommonAreaElectricityReadingUrl,
  updateCommonAreaElectricityReadingURL,
  getCommonAreaElectricityReadingsUrl
} from '../../../../../utils/urls';

const ElectricityTab = () => {
  const [electricityData, setElectricityData] = useState([]);
  const [allElectricityData, setAllElectricityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [electricityDialogVisible, setElectricityDialogVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedElectricityItem, setSelectedElectricityItem] = useState(null);
  const facilityId = useFacilityStore((state) => state.facilityId);
  
  const [electricityForm, setElectricityForm] = useState({
    location: 'Common Area',
    openingReadingKWh: '',
    closingReadingKWh: '',
    consumptionKWh: '',
    total: '',
    date: new Date().toISOString().split('T')[0]
  });

  const electricityLocationOptions = [
    { label: 'Common Area', value: 'Common Area' },
    { label: 'Lift', value: 'Lift' },
    { label: 'Pumps', value: 'Pumps' }
  ];

  useEffect(() => {
    if (facilityId) {
      fetchData();
    }
  }, [facilityId]);

  const fetchData = async () => {
    if (!facilityId) return;

    setLoading(true);
    try {
      const electricityResponse = await makeRequest2(
        `${getCommonAreaElectricityReadingsUrl}/${facilityId}`,
        'GET'
      );
      
      if (
        electricityResponse.success &&
        electricityResponse.data &&
        electricityResponse.data.readings
      ) {
        const readings = electricityResponse.data.readings;
        const data = Array.isArray(readings) ? readings : [readings];
        setAllElectricityData(data); 
        setElectricityData(data);
      }
    } catch (error) {
      toastify("Failed to load electricity data", "error");
    } finally {
      setLoading(false);
    }
  };

  const filterDataByDateRange = () => {
    if (!startDate && !endDate) {
      setElectricityData(allElectricityData);
      return;
    }

    // Filter data by selected date range
    const filteredData = allElectricityData.filter(item => {
      const itemDate = new Date(item.date);
      
      if (startDate && !endDate) {
        return itemDate >= startDate;
      }
      
      if (!startDate && endDate) {
        return itemDate <= endDate;
      }
      return itemDate >= startDate && itemDate <= endDate;
    });
    setElectricityData(filteredData);
  };

  // Calculate electricity consumption
  const calculateElectricityConsumption = (opening, closing, setter, form) => {
    if (opening !== '' && closing !== '') {
      const openingVal = parseFloat(opening);
      const closingVal = parseFloat(closing);

      if (!isNaN(openingVal) && !isNaN(closingVal) && closingVal >= openingVal) {
        const consumption = closingVal - openingVal;
        const newForm = {
          ...form,
          consumptionKWh: consumption.toFixed(2),
          total: consumption.toFixed(2) 
        };
        setter(newForm);
      }
    }
  };

  // Reset form values
  const resetElectricityForm = () => {
    setElectricityForm({
      location: 'Common Area',
      openingReadingKWh: '',
      closingReadingKWh: '',
      consumptionKWh: '',
      total: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const openAddDialog = () => {
    setIsEditMode(false);
    resetElectricityForm();
    setElectricityDialogVisible(true);
  };

  const openEditDialog = (item) => {
    setIsEditMode(true);
    setSelectedElectricityItem(item);
    setElectricityForm({
      location: item.location,
      openingReadingKWh: item.openingReadingKWh.toString(),
      closingReadingKWh: item.closingReadingKWh.toString(),
      consumptionKWh: item.consumptionKWh.toString(),
      total: item.total?.toString() || item.consumptionKWh.toString(),
      date: new Date(item.date).toISOString().split('T')[0]
    });
    setElectricityDialogVisible(true);
  };

  const handleElectricitySave = async () => {
    if (!facilityId) {
      toastify("Facility ID is required", "error");
      return;
    }

    if (!electricityForm.openingReadingKWh || !electricityForm.closingReadingKWh || !electricityForm.location) {
      toastify("Please fill all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...electricityForm,
        openingReadingKWh: parseFloat(electricityForm.openingReadingKWh),
        closingReadingKWh: parseFloat(electricityForm.closingReadingKWh),
        consumptionKWh: parseFloat(electricityForm.consumptionKWh)
      };

      let response;
      if (isEditMode && selectedElectricityItem) {
        response = await makeRequest2(
          `${updateCommonAreaElectricityReadingURL}/${facilityId}/${selectedElectricityItem._id}`,
          "PUT",
          payload
        );
      } else {
        response = await makeRequest2(
          `${addCommonAreaElectricityReadingUrl}/${facilityId}`,
          "POST",
          payload
        );
      }

      if (response.success) {
        toastify(`Electricity reading ${isEditMode ? 'updated' : 'added'} successfully`, "success");
        setElectricityDialogVisible(false);
        resetElectricityForm();
        fetchData();
      } else {
        toastify(response.message || `Failed to ${isEditMode ? 'update' : 'add'} electricity reading`, "error");
      }
    } catch (error) {
      toastify(`Error ${isEditMode ? 'updating' : 'adding'} electricity reading`, "error");
    } finally {
      setLoading(false);
    }
  };

  const electricityActionTemplate = (rowData) => {
    return (
      <div>
        <Button
          icon="ti ti-pencil"
          className="p-button-rounded p-button-info"
          onClick={() => openEditDialog(rowData)}
          tooltip="Edit Electricity Reading"
        />
      </div>
    );
  };
  const formatDate = (rowData) => {
    if (!rowData.date) return '';
    const date = new Date(rowData.date);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Dialog Footer
  const electricityDialogFooter = (
    <div className="text-end mt-4">
      <Button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setElectricityDialogVisible(false)}
        disabled={loading}
      >
        Cancel
      </Button>
      &nbsp;
      <Button
        type="button"
        className="btn btn-primary"
        onClick={handleElectricitySave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <>
      <div className="mb-4">
        <div className="card">
          <div className="card-body d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="me-2">Date Range:</span>
              <Calendar
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                showIcon
                placeholder="Start Date"
                className="me-2"
              />
              <span className="me-2">to</span>
              <Calendar
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                showIcon
                placeholder="End Date"
                className="me-2"
              />
              <Button
                icon="ti ti-search"
                className="ms-2"
                onClick={filterDataByDateRange}
                tooltip="Search"
              />
              <Button
                icon="ti ti-refresh"
                className="ms-2"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setElectricityData(allElectricityData);
                }}
                tooltip="Reset"
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={openAddDialog}
            >
              Add Electricity Reading
            </button>
          </div>
        </div>
      </div>

      <DataTable
        value={electricityData}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="No electricity data found"
        stripedRows
        sortMode="multiple"
        removableSort
        tableStyle={{ minWidth: '50rem' }}
      >
        <Column field="location" header="Location" sortable />
        <Column field="openingReadingKWh" header="Opening Reading (kWh)" sortable />
        <Column field="closingReadingKWh" header="Closing Reading (kWh)" sortable />
        <Column field="consumptionKWh" header="Consumption (kWh)" sortable />
        <Column body={formatDate} header="Date" sortable field="date" />
        <Column header="Actions" body={electricityActionTemplate} />
      </DataTable>

      {/* Electricity Dialog */}
      <Dialog
        visible={electricityDialogVisible}
        style={{ width: '450px' }}
        header={isEditMode ? "Edit Electricity Reading" : "Add Electricity Reading"}
        modal
        className="p-fluid"
        footer={electricityDialogFooter}
        onHide={() => setElectricityDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="location">Location</label>
          <Dropdown
            id="location"
            value={electricityForm.location}
            options={electricityLocationOptions}
            onChange={(e) => setElectricityForm({ ...electricityForm, location: e.value })}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="openingReadingKWh">Opening Reading (kWh)</label>
          <InputText
            id="openingReadingKWh"
            value={electricityForm.openingReadingKWh}
            onChange={(e) => {
              const newForm = { ...electricityForm, openingReadingKWh: e.target.value };
              setElectricityForm(newForm);
              calculateElectricityConsumption(e.target.value, electricityForm.closingReadingKWh, setElectricityForm, newForm);
            }}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="closingReadingKWh">Closing Reading (kWh)</label>
          <InputText
            id="closingReadingKWh"
            value={electricityForm.closingReadingKWh}
            onChange={(e) => {
              const newForm = { ...electricityForm, closingReadingKWh: e.target.value };
              setElectricityForm(newForm);
              calculateElectricityConsumption(electricityForm.openingReadingKWh, e.target.value, setElectricityForm, newForm);
            }}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="consumptionKWh">Consumption (kWh)</label>
          <InputText
            id="consumptionKWh"
            value={electricityForm.consumptionKWh}
            onChange={(e) => setElectricityForm({ ...electricityForm, consumptionKWh: e.target.value })}
            required
            disabled={true}
          />
        </div>
        <div className="field">
          <label htmlFor="date">Date</label>
          <InputText
            id="date"
            type="date"
            value={electricityForm.date}
            onChange={(e) => setElectricityForm({ ...electricityForm, date: e.target.value })}
            required
          />
        </div>
      </Dialog>
    </>
  );
};

export default ElectricityTab;